/**
 * 动作可用性判定（决策 14.3 / ADR-005）。
 *
 * `enabledForCurrentInjury` 是**派生字段**（不持久化），由本函数按
 * 「动作结构化标签 + InjuryConstraint + 条件性规则 + 医疗锁」确定性计算。
 *
 * 铁律：
 *   - `lockedUntilMedicalClearance = true` 是持久化硬锁：无论其它标签如何都默认不可用，
 *     且不能靠普通编辑/替换解锁（只能由用户在伤情设置中确认医疗许可后手动解锁）。
 *   - 条件性动作必须用结构化 `conditionalRules` 判定（决策 14.3），不用自然语言描述。
 *   - 任何标签都不是「绝对零负荷」断言；本函数只做保守的确定性过滤。
 *   - 规则版本随 RULES_VERSION 审计。
 *
 * 条件规则语义（failureBehavior 为 disable 或 require_confirmation）：
 *   规则描述的是「该动作在当前伤情下按预期部署，需要满足的现场条件」。
 *   由于应用无法在数字层验证健身房实际器械/站位，条件不满足时按保守原则处理：
 *     - disable                → 当前伤情下不可用（硬性不可用）
 *     - require_confirmation   → 可用，但标记为「需用户确认」（替代选择时按相对风险处理）
 */

import type {
  ConditionalExerciseRule,
  DerivedExerciseAvailability,
  Exercise,
  InjuryConstraint,
  LoadLevel,
  RestrictionLevel,
} from "@jilian/shared";
import { RULES_VERSION } from "@jilian/shared";

// ---------------------------------------------------------------------------
// 等级偏序（索引越小越严格/越低）
// ---------------------------------------------------------------------------

const LOAD_RANK: Record<LoadLevel, number> = {
  none_or_minimal: 0,
  low: 1,
  medium: 2,
  high: 3,
};

const BALANCE_RANK: Record<Exercise["balanceDemand"], number> = {
  low: 0,
  medium: 1,
  high: 2,
};

/** 伤情前足限制 → 允许的最大前足负荷（保守）。 */
const FOREFOOT_ALLOWED_LOAD: Record<RestrictionLevel, LoadLevel> = {
  prohibited: "none_or_minimal",
  strongly_limited: "none_or_minimal",
  limited: "low",
  unrestricted: "high",
};

/** 伤情脚跟限制 → 允许的最大脚跟负荷（保守）。 */
const HEEL_ALLOWED_LOAD: Record<RestrictionLevel, LoadLevel> = {
  prohibited: "none_or_minimal",
  strongly_limited: "none_or_minimal",
  limited: "low",
  unrestricted: "high",
};

// ---------------------------------------------------------------------------
// 条件性规则的确定性判定（每种条件的「满足」定义见注释）
// ---------------------------------------------------------------------------

export function conditionSatisfied(
  rule: ConditionalExerciseRule,
  exercise: Exercise,
  injury: InjuryConstraint,
): boolean {
  switch (rule.conditionType) {
    case "requires_no_forefoot_drive": {
      // 无法验证现场器械是否确实可以无前足蹬踏使用；仅当前足完全不受限时才视为满足。
      // §15：坐姿器械划船「仅在不需要前足主动蹬踏时启用」。
      return injury.forefootRestriction === "unrestricted";
    }
    case "requires_seated_setup": {
      // 无法验证设备是否为坐姿/高度稳定版本；仅当伤情对站姿/前足/平衡均无限制时才视为满足。
      return (
        injury.balanceDemandAllowed === "unrestricted" &&
        injury.weightBearingLevel === "full_weight_bearing" &&
        injury.forefootRestriction === "unrestricted"
      );
    }
    case "requires_low_balance_demand": {
      // 低平衡需求：伤情允许「低」及以上即可满足。
      return injury.balanceDemandAllowed !== "low_only";
    }
    case "requires_no_injury_contact": {
      // 动作不得接触伤处：默认满足；仅当 contraindicationTags 显式标记「接触/压力」且
      // 命中伤处关键词（侧别/脚/趾/踝）时失败（保守近似，宁可多过滤不漏放）。
      const contactTag = exercise.contraindicationTags.find((t) => t.includes("接触") || t.includes("压力"));
      if (!contactTag) return true;
      const sideKw = injury.side === "right" ? "右" : injury.side === "left" ? "左" : "";
      const keywords = [sideKw, "脚", "趾", "踝"].filter(Boolean);
      return !keywords.some((k) => contactTag.includes(k));
    }
    case "requires_safe_mounting": {
      // 上下器械要求：器械上下难度不高、且当前允许部分承重及以上、前足未被完全禁止。
      return (
        exercise.mountingDifficulty !== "high" &&
        injury.weightBearingLevel !== "non_weight_bearing" &&
        injury.forefootRestriction !== "prohibited"
      );
    }
    case "requires_medical_clearance": {
      // 需要医疗许可：仅当伤情达到最高许可阶段（3 = 已解锁）时满足。
      return injury.medicalClearanceStage >= 3;
    }
  }
}

// ---------------------------------------------------------------------------
// 结构化硬过滤（条件规则之外的基础标签过滤）
// ---------------------------------------------------------------------------

export interface StructuralViolation {
  reasonCode: string;
  detail: string;
}

export function structuralViolations(exercise: Exercise, injury: InjuryConstraint): StructuralViolation[] {
  const out: StructuralViolation[] = [];

  if (exercise.lockedUntilMedicalClearance) {
    out.push({
      reasonCode: "LOCKED_UNTIL_MEDICAL_CLEARANCE",
      detail: "医疗许可前锁定，不能用普通替换/编辑解锁。",
    });
  }

  const allowedForefoot = FOREFOOT_ALLOWED_LOAD[injury.forefootRestriction];
  if (LOAD_RANK[exercise.forefootLoad] > LOAD_RANK[allowedForefoot]) {
    out.push({
      reasonCode: "FOREFOOT_LOAD_TOO_HIGH",
      detail: `前足负荷 ${exercise.forefootLoad} 超出当前伤情允许（≤${allowedForefoot}）。`,
    });
  }

  const allowedHeel = HEEL_ALLOWED_LOAD[injury.heelRestriction];
  if (LOAD_RANK[exercise.heelLoad] > LOAD_RANK[allowedHeel]) {
    out.push({
      reasonCode: "HEEL_LOAD_TOO_HIGH",
      detail: `脚跟负荷 ${exercise.heelLoad} 超出当前伤情允许（≤${allowedHeel}）。`,
    });
  }

  // 平衡需求上限
  const allowedBalanceMax =
    injury.balanceDemandAllowed === "low_only" ? 0 : injury.balanceDemandAllowed === "low_to_medium" ? 1 : 2;
  if (BALANCE_RANK[exercise.balanceDemand] > allowedBalanceMax) {
    out.push({
      reasonCode: "BALANCE_DEMAND_TOO_HIGH",
      detail: `平衡需求 ${exercise.balanceDemand} 超出当前伤情允许。`,
    });
  }

  // 站姿动作：需要稳定的支撑基础；当前伤情下保守处理
  if (exercise.posture === "standing") {
    const supported =
      injury.weightBearingLevel === "full_weight_bearing" ||
      injury.weightBearingLevel === "weight_bearing_as_tolerated";
    const balanceOk = exercise.balanceDemand === "low";
    if (!supported || !balanceOk) {
      out.push({
        reasonCode: "STANDING_POSTURE_NOT_SUPPORTED",
        detail: `站姿动作（平衡需求 ${exercise.balanceDemand}）在当前承重阶段（${injury.weightBearingLevel}）下不推荐。`,
      });
    }
  }

  // 需要搬运/移动负重（如搬哑铃、插拔杠铃片行走）
  if (exercise.requiresWeightCarrying) {
    const canCarry =
      injury.weightBearingLevel === "full_weight_bearing" ||
      injury.weightBearingLevel === "weight_bearing_as_tolerated";
    if (!canCarry) {
      out.push({
        reasonCode: "WEIGHT_CARRYING_NOT_ALLOWED",
        detail: "动作需要搬运负重，当前承重阶段不允许。",
      });
    }
  }

  // 需要搭档（独自训练时不默认禁用，仅作为风险提示，不参与硬过滤）
  return out;
}

// ---------------------------------------------------------------------------
// 主入口：派生可用性
// ---------------------------------------------------------------------------

export function evaluateExerciseEligibility(input: {
  exercise: Exercise;
  injury: InjuryConstraint;
}): DerivedExerciseAvailability {
  const { exercise, injury } = input;
  const reasonCodes: string[] = [];
  const unmetConditions: string[] = [];

  const violations = structuralViolations(exercise, injury);
  for (const v of violations) reasonCodes.push(v.reasonCode);

  for (const rule of exercise.conditionalRules) {
    if (!conditionSatisfied(rule, exercise, injury)) {
      unmetConditions.push(rule.id);
      reasonCodes.push(`CONDITION_UNMET:${rule.conditionType}`);
    }
  }

  const isEnabled =
    violations.length === 0 &&
    exercise.conditionalRules.every(
      (rule) => conditionSatisfied(rule, exercise, injury) || rule.failureBehavior === "require_confirmation",
    );

  return {
    exerciseId: exercise.id,
    exerciseVersion: exercise.version,
    isEnabledForCurrentInjury: isEnabled,
    unmetConditions,
    reasonCodes,
  };
}

/** 全部条件的满足情况（供生成器/替代候选做「可确认」判定）。 */
export function requiresUserConfirmation(exercise: Exercise, injury: InjuryConstraint): boolean {
  return exercise.conditionalRules.some(
    (rule) => !conditionSatisfied(rule, exercise, injury) && rule.failureBehavior === "require_confirmation",
  );
}

export { RULES_VERSION };